import { Alert, SimpleGrid, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { dashboardApi } from '../../../api/dashboard';
import { useGroup } from '../../../contexts/GroupContext';
import { useGroupQuery } from '../../../hooks/useGroupQuery';
import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { PerformanceCardSkeleton } from '../PerformanceCardSkeleton';
import { MAX_QUARTERS_CHART } from '../constants';
import type { InTulsaActivityPoint, InTulsaAllTime } from './types';
import { AdHocCard } from './cards/AdHocCard';
import { MajorReportsCard } from './cards/MajorReportsCard';
import { PresentationSubmissionsCard } from './cards/PresentationSubmissionsCard';
import { PresentationsCard } from './cards/PresentationsCard';

type InTulsaAswpPerformanceMetricsProps = {
  selectedGroup?: string | null;
};

const asNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

function mapApiToInTulsaData(data: DashboardMetricsResponse): {
  activity: InTulsaActivityPoint[];
  allTime: InTulsaAllTime;
} | null {
  const section = data.sections.find((entry) => entry.sectionId === 'inTulsaAswp');
  if (!section) return null;
  const activity = section.metrics.find((metric) => metric.metricId === 'inTulsaAswp.activity');
  if (!activity) return null;

  const mappedActivity: InTulsaActivityPoint[] = activity.series
    .slice(-MAX_QUARTERS_CHART)
    .map((point) => ({
    quarter: String(point.quarter),
    majorReportsCompleted: asNumber(point.majorReportsCompleted),
    adHocRequestsCompleted: asNumber(point.adHocRequestsCompleted),
    presentationSubmissions: asNumber(point.presentationSubmissions),
    presentationsDelivered: asNumber(point.presentationsDelivered),
    qoq: (point.qoq ?? {}) as Record<string, unknown>,
    yoy: (point.yoy ?? {}) as Record<string, unknown>,
  }));

  const mappedAllTime: InTulsaAllTime = {
    majorReportsCompleted: asNumber(activity.allTime?.majorReportsCompleted),
    adHocRequestsCompleted: asNumber(activity.allTime?.adHocRequestsCompleted),
    presentationSubmissions: asNumber(activity.allTime?.presentationSubmissions),
    presentationsDelivered: asNumber(activity.allTime?.presentationsDelivered),
  };

  return { activity: mappedActivity, allTime: mappedAllTime };
}

export function InTulsaAswpPerformanceMetrics({
  selectedGroup,
}: InTulsaAswpPerformanceMetricsProps) {
  const { selectedGroup: contextGroup } = useGroup();
  const activeGroup = selectedGroup || contextGroup;
  const { data, error, isLoading } = useGroupQuery(
    ['dashboard-metrics', 'intulsa-aswp'],
    () => dashboardApi.getDashboardMetrics(activeGroup || 'inTulsa - ASWP'),
    { enabled: activeGroup === 'inTulsa - ASWP' }
  );

  const inTulsaData = useMemo(() => (data ? mapApiToInTulsaData(data) : null), [data]);
  const hasApiData = inTulsaData !== null && inTulsaData.activity.length > 0;
  const series =
    activeGroup === 'inTulsa - ASWP' && inTulsaData
      ? inTulsaData.activity
      : inTulsaData?.activity ?? [];

  return (
    <Stack spacing="lg">
      {error ? (
        <Alert color="yellow" variant="light">
          Unable to load dashboard API data.
        </Alert>
      ) : null}
      {!isLoading && !hasApiData ? (
        <Alert color="yellow" variant="light">
          No dashboard API data returned for this section.
        </Alert>
      ) : null}
      {isLoading && !hasApiData ? (
        <>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
            <PerformanceCardSkeleton />
            <PerformanceCardSkeleton />
          </SimpleGrid>
        </>
      ) : null}
      {hasApiData && inTulsaData ? (
        <>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
            <MajorReportsCard series={series} allTime={inTulsaData.allTime} />
            <AdHocCard series={series} allTime={inTulsaData.allTime} />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 980, cols: 1 }]}>
            <PresentationSubmissionsCard series={series} allTime={inTulsaData.allTime} />
            <PresentationsCard series={series} allTime={inTulsaData.allTime} />
          </SimpleGrid>
        </>
      ) : null}
    </Stack>
  );
}
